import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Search, Users, RefreshCw } from 'lucide-react'
import { api } from '@/lib/api'

interface ClientRow {
  id: string
  displayName: string
  email: string | null
  phoneE164: string | null
  tags: string[]
  lastActivityAt: string | null
  clientTags: { id: string; tag: { id: string; name: string; color: string | null } }[]
  _count: { conversations: number; activityLog: number }
}

interface SmartList {
  id: string
  name: string
  contactCountCached: number
}

export function ClientsPage() {
  const [items, setItems] = useState<ClientRow[]>([])
  const [smartLists, setSmartLists] = useState<SmartList[]>([])
  const [q, setQ] = useState('')
  const [selectedSmartList, setSelectedSmartList] = useState<string>('')
  const [loading, setLoading] = useState(true)

  async function load() {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (q.trim()) params.set('q', q.trim())
      if (selectedSmartList) params.set('smartListId', selectedSmartList)
      const [list, sl] = await Promise.all([
        api<{ items: ClientRow[] }>(`/v1/marketing/clients?${params.toString()}`),
        api<{ items: SmartList[] }>('/v1/marketing/smart-lists'),
      ])
      setItems(list.items)
      setSmartLists(sl.items)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSmartList])

  function onSubmitSearch(e: React.FormEvent) {
    e.preventDefault()
    void load()
  }

  return (
    <div className="p-6 max-w-6xl">
      <header className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-lg font-medium tracking-tight flex items-center gap-2">
            <Users className="size-5 text-blueprint-700" />
            Clientes
          </h1>
          <p className="text-xs font-mono text-ink-700/60">
            ordenados por última actividad · filtra con SmartList o buscador
          </p>
        </div>
        <Link to="/marketing/smart-lists" className="bp-btn">
          SmartLists
        </Link>
      </header>

      <div className="flex gap-2 mb-3">
        <form onSubmit={onSubmitSearch} className="flex-1 flex">
          <div className="relative flex-1">
            <Search className="size-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-ink-700/40" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="buscar por nombre, email, teléfono…"
              className="w-full pl-7 pr-3 py-1.5 border border-ink-900/15 rounded text-sm"
            />
          </div>
          <button className="bp-btn ml-2" type="submit" disabled={loading}>
            {loading ? '…' : 'Buscar'}
          </button>
        </form>
        <select
          value={selectedSmartList}
          onChange={(e) => setSelectedSmartList(e.target.value)}
          className="border border-ink-900/15 rounded px-2 py-1.5 text-sm bg-white"
        >
          <option value="">— Sin SmartList —</option>
          {smartLists.map((sl) => (
            <option key={sl.id} value={sl.id}>
              {sl.name}
              {sl.contactCountCached > 0 ? ` (${sl.contactCountCached})` : ''}
            </option>
          ))}
        </select>
        <button className="bp-btn" onClick={() => void load()} disabled={loading}>
          <RefreshCw className="size-3.5" />
        </button>
      </div>

      <div className="bp-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-alt text-xs font-mono uppercase tracking-wider text-ink-700/60">
            <tr>
              <th className="px-3 py-2 text-left">Cliente</th>
              <th className="px-3 py-2 text-left">Canales</th>
              <th className="px-3 py-2 text-left">Tags</th>
              <th className="px-3 py-2 text-right">Hilos</th>
              <th className="px-3 py-2 text-right">Última actividad</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading && (
              <tr>
                <td colSpan={5} className="px-3 py-8 text-center text-ink-700/50 text-xs">
                  Sin resultados.
                </td>
              </tr>
            )}
            {items.map((c) => (
              <tr key={c.id} className="border-t border-ink-900/10 hover:bg-surface-alt/50">
                <td className="px-3 py-2">
                  <Link to={`/marketing/clients/${c.id}`} className="text-blueprint-700 font-medium">
                    {c.displayName}
                  </Link>
                </td>
                <td className="px-3 py-2 font-mono text-xs text-ink-700">
                  <div>{c.email ?? '—'}</div>
                  <div className="text-ink-700/60">{c.phoneE164 ?? ''}</div>
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {c.clientTags.map((ct) => (
                      <span
                        key={ct.id}
                        className="px-1.5 py-0.5 text-[10px] rounded border"
                        style={{
                          borderColor: ct.tag.color ?? '#cbd5e1',
                          color: ct.tag.color ?? '#475569',
                        }}
                      >
                        {ct.tag.name}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-3 py-2 text-right font-mono text-xs">
                  {c._count.conversations}
                </td>
                <td className="px-3 py-2 text-right font-mono text-xs text-ink-700">
                  {c.lastActivityAt
                    ? new Date(c.lastActivityAt).toLocaleString('es-ES')
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
